import { html, nothing, type TemplateResult } from "lit";
import type { HistoryEntry, PublicCard, PublicState } from "./protocol";
import { artUrl } from "./art-url";
import { cardsIn } from "./board/field";
import { isCreatureCard, isLandCard, optionValue, playerName } from "./board/game-text";

// What the end of a game shows: who won, the card that carried them, and each player's game in
// numbers, with their deck and the permanents they finished with.
export type GameOver = {
  state: PublicState;
  history: HistoryEntry[];
  // Null for a draw.
  winner: string | null;
  // The viewer's seat; null for a spectator.
  seat: string | null;
  deckOf: (seat: string) => string | null;
  mulligansOf: (seat: string) => number;
  backToRooms: () => void;
  // What the game earned the viewer, under the result.
  earned?: TemplateResult | typeof nothing;
  review?: TemplateResult;
};

type SeatNumbers = { spells: number; lands: number; attackers: number };

// A seat's game from the log: spells cast, lands played, and creatures sent to attack.
function seatNumbers(history: HistoryEntry[], seat: string): SeatNumbers {
  const out: SeatNumbers = { spells: 0, lands: 0, attackers: 0 };
  for (const e of history) {
    if (e.seat !== seat) continue;
    const a = e.answer;
    if (a.kind === "Take") {
      const kind = a.values[0].kind;
      if (kind === "CastSpell" || kind === "CastAlternative" || kind === "CastBackFace") out.spells++;
      else if (kind === "PlayLand" || kind === "PlayLandBack") out.lands++;
    } else if (a.kind === "Attackers") out.attackers += a.values[0].length;
  }
  return out;
}

// The permanents a player finished with that tell their game: commanders first, then creatures by
// power, then their other nonland permanents.
function keyPermanents(state: PublicState, seat: string): PublicCard[] {
  const p = state.players[seat];
  if (!p) return [];
  const power = (c: PublicCard) => optionValue(c.characteristics.power) ?? -1;
  const rank = (c: PublicCard) => (c.is_commander ? 0 : isCreatureCard(c) ? 1 : 2);
  return cardsIn(p.battlefield)
    .filter((c) => c.facing.kind === "FaceUp" && !isLandCard(c))
    .sort((a, b) => rank(a) - rank(b) || power(b) - power(a));
}

export function renderGameOver(g: GameOver): TemplateResult {
  const st = g.state;
  const winnerName = g.winner === null ? null : playerName(st, g.winner);
  const viewerWon = g.winner !== null && g.winner === g.seat;
  const title = winnerName === null ? "Draw" : g.seat === null ? `${winnerName} won` : viewerWon ? "Victory" : "Defeat";
  const mood = winnerName === null ? "draw" : g.seat !== null && !viewerWon ? "defeat" : "victory";
  const featured = g.winner === null ? undefined : keyPermanents(st, g.winner)[0];
  const when = `on turn ${st.round}`;
  const drawn = optionValue(st.drawn);
  const subtitle = winnerName === null ? `Drawn ${when}${drawn === null ? "" : `: ${drawn}`}`
    : `${viewerWon ? "You" : winnerName} won ${when}${featured ? `, with ${featured.characteristics.name}` : ""}`;
  return html`
    <div class="winner ${mood}" style=${featured ? `--art: url("${artUrl(featured.characteristics.name)}")` : ""}>
      <div class="winner-text">${title}<small>${subtitle}</small>${g.earned ?? nothing}</div>
      ${featured ? html`<div class="winner-card"><card-art name=${featured.characteristics.name} .keywords=${featured.characteristics.keywords}></card-art></div>` : nothing}
    </div>
    <div class="final-seats">
      ${st.turn_order.map((pid) => {
        const p = st.players[pid];
        if (!p) return nothing;
        const n = seatNumbers(g.history, pid);
        const cards = keyPermanents(st, pid).slice(0, 6);
        return html`<section class="final-seat ${pid === g.winner ? "won" : ""}" aria-label=${p.name}>
          <header>
            <span class="seat-face ${pid === g.seat ? "you" : ""}">${(p.name || pid).charAt(0).toUpperCase()}</span>
            <div class="seat-who">
              <b>${p.name}${pid === g.seat ? html` <span class="hint">(you)</span>` : nothing}</b>
              <span class="seat-deck">${g.deckOf(pid) ?? "Deck unknown"}</span>
            </div>
            ${pid === g.winner ? html`<span class="seat-tag won">Winner</span>` : p.lost ? html`<span class="seat-tag">Lost</span>` : nothing}
          </header>
          <dl class="numbers">
            <div><dt>Life</dt><dd>${p.life}</dd></div>
            <div><dt>Spells cast</dt><dd>${n.spells}</dd></div>
            <div><dt>Lands played</dt><dd>${n.lands}</dd></div>
            <div><dt>Attackers sent</dt><dd>${n.attackers}</dd></div>
            <div><dt>Mulligans</dt><dd>${g.mulligansOf(pid)}</dd></div>
          </dl>
          ${cards.length > 0
            ? html`<div class="final-cards">${cards.map((c) => html`<card-art name=${c.characteristics.name} .keywords=${c.characteristics.keywords} title=${c.characteristics.name}></card-art>`)}</div>`
            : html`<p class="none">Nothing left on the battlefield but lands.</p>`}
        </section>`;
      })}
    </div>
    ${g.review ?? nothing}
    <div class="lobby-row room-actions-after"><button class="btn" @click=${g.backToRooms}>Back to rooms</button></div>
  `;
}
